/**
 * Page Views Tracking Hook
 * Tracks views on public pages and fetches view statistics
 */
import { useEffect, useState, useCallback } from 'react';

const API = '/api';

/**
 * Track a page view (call once on page load)
 */
export async function trackPageView(pageType, pageSlug) {
  try {
    await fetch(`${API}/public/view`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        page_type: pageType,
        page_slug: pageSlug,
        referrer: document.referrer || '',
        user_agent: navigator.userAgent || ''
      })
    });
  } catch (e) {
    // Silent fail - don't break the page
    console.debug('View tracking failed:', e);
  }
}

/**
 * Hook to track page view and get stats
 * @param {string} pageType - "kennel", "litter", "stud", "dog"
 * @param {string} pageSlug - The page slug
 * @returns {object} - { totalViews, weeklyViews, isLoading }
 */
export function usePageViews(pageType, pageSlug) {
  const [stats, setStats] = useState({
    totalViews: 0,
    weeklyViews: 0,
    dailyBreakdown: [],
    lastView: null,
    isLoading: true
  });
  
  useEffect(() => {
    if (!pageType || !pageSlug) return;
    
    // Track this view
    trackPageView(pageType, pageSlug);
    
    // Fetch stats
    const fetchStats = async () => {
      try {
        const res = await fetch(`${API}/public/views/${pageType}/${pageSlug}`);
        if (res.ok) {
          const data = await res.json();
          setStats({
            totalViews: data.total_views || 0,
            weeklyViews: data.weekly_views || 0,
            dailyBreakdown: data.daily_breakdown || [],
            lastView: data.last_view || null,
            isLoading: false
          });
        }
      } catch (e) {
        console.debug('Failed to fetch view stats:', e);
        setStats(prev => ({ ...prev, isLoading: false }));
      }
    };
    
    fetchStats();
  }, [pageType, pageSlug]);
  
  return stats;
}

/**
 * Hook to get all view stats for the current user's pages
 * @returns {object} - { summary, kennels, litters, studs, isLoading }
 */
export function useMyViewStats() {
  const [stats, setStats] = useState({
    summary: { total_views: 0, weekly_views: 0 },
    kennels: { total: 0, weekly: 0, by_page: [] },
    litters: { total: 0, weekly: 0, by_page: [] },
    studs: { total: 0, weekly: 0, by_page: [] },
    isLoading: true,
    error: null
  });
  
  const fetchStats = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setStats(prev => ({ ...prev, isLoading: false, error: 'Not logged in' }));
      return;
    }
    
    try {
      const res = await fetch(`${API}/stats/my-views`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setStats({
          summary: data.summary || { total_views: 0, weekly_views: 0 },
          kennels: data.kennels || { total: 0, weekly: 0, by_page: [] },
          litters: data.litters || { total: 0, weekly: 0, by_page: [] },
          studs: data.studs || { total: 0, weekly: 0, by_page: [] },
          isLoading: false,
          error: null
        });
      } else {
        throw new Error('Failed to fetch stats');
      }
    } catch (e) {
      console.debug('Failed to fetch my view stats:', e);
      setStats(prev => ({ ...prev, isLoading: false, error: e.message }));
    }
  }, []);
  
  useEffect(() => {
    fetchStats();
  }, [fetchStats]);
  
  return { ...stats, refetch: fetchStats };
}

export default { trackPageView, usePageViews, useMyViewStats };
